import { Overlay } from '@angular/cdk/overlay';
import { ComponentType } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy, Renderer2, RendererFactory2, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { HelperService } from 'utils/helper';
import { getDefaultModalSettings } from '../utils/helpers';

@Injectable({
  providedIn: 'root',
})
export class OpenDialogService implements OnDestroy {
  openSubscription = new Subscription();
  closeSubscription = new Subscription();
  hasShrink = true;
  private renderer: Renderer2;
  constructor(
    private dialog: MatDialog,
    private overlay: Overlay,
    @Inject(DOCUMENT) private document: Document,
    private rendererFactory2: RendererFactory2,
    private helperService: HelperService,
    private ngZone: NgZone,
  ) {
    this.renderer = this.rendererFactory2.createRenderer(null, null);
  }
  openDialog<T, D = any, R = any>(
    dialogClass: ComponentType<T> | TemplateRef<T>,
    config?: MatDialogConfig<D>,
    isFullScreen?: boolean,
  ) {
    if (this.helperService.isPlatformBrowser()) {
      const fullScreenOption: MatDialogConfig<T> = {
        width: '100vw',
        maxWidth: '100vw',
        minWidth: '100vw',
        height: '100%',
      };
      Object.assign(config, isFullScreen || this.helperService.isMobileNotIpad() ? fullScreenOption : {});

      const dialogRef: MatDialogRef<T, R> = this.ngZone.run(() =>
        this.dialog.open<T, D, R>(dialogClass, {
          ...getDefaultModalSettings(),
          ...config,
          scrollStrategy: this.overlay.scrollStrategies.block(),
          backdropClass: 'sharryland-backdrop',
          disableClose: true,
          panelClass: [
            ...(config.panelClass || []),
            'panel_animated',
            'hidden',
            isFullScreen || this.helperService.isMobileNotIpad() ? 'full-screen' : '',
          ],
        }),
      );
      if (isFullScreen || this.helperService.isMobileNotIpad()) {
        this.openSubscription = dialogRef.afterOpened().subscribe({
          next: () => {
            this.hasShrink = true;
            if (!this.document.querySelector('html').classList.contains('shrink')) {
              this.hasShrink = false;
              this.renderer.addClass(this.document.querySelector('html'), 'shrink');
            }
            this.renderer.removeClass(this.document.querySelector('html'), 'cdk-global-scrollblock');
            this.renderer.addClass(this.document.querySelector('.app-wrapper'), 'cdk-global-scrollblock');
            this.renderer.addClass(this.document.body, 'cdk-sharry-scrollblock');
            dialogRef.removePanelClass('hidden');
          },
        });
        this.closeSubscription = dialogRef.beforeClosed().subscribe({
          next: () => {
            if (!this.hasShrink) {
              this.renderer.removeClass(this.document.querySelector('html'), 'shrink');
            }
            if (this.document.querySelector('.app-wrapper')?.classList) {
              this.renderer.removeClass(this.document.querySelector('.app-wrapper'), 'cdk-global-scrollblock');
            }
            if (this.document.body?.classList) {
              this.renderer.removeClass(this.document.body, 'cdk-sharry-scrollblock');
            }
          },
        });
      } else {
        dialogRef.removePanelClass('hidden');
      }
      return dialogRef;
    }
    return null;
  }
  ngOnDestroy(): void {
    this.openSubscription.unsubscribe();
    this.closeSubscription.unsubscribe();
  }
}
